<template>
  <div>
    <v-select
      v-if="riskTypes"
      v-model="selectedRating"
      :items="riskTypes"
      item-text="name"
      item-value="id"
      :menu-props="{ maxHeight: '400' }"
      :label="label"
      :placeholder="placeholder"
      return-object
      :clearable="clearable"
      :multiple="multiple"
      class="rating-list"
      @change="handleChangeRating"
      @click:clear="handleClickClearRating"
    >
      <template v-slot:selection="{ item, index }">
        <v-chip v-if="chips && item" small>{{ item.display }}</v-chip>
        <div v-if="!chips && item && !multiple" class="v-select__slot">
          <div class="v-select__selections">
            <div class="v-select__selection v-select__selection--comma">
              {{ item.display }}
            </div>
          </div>
        </div>
        <span v-if="index === 0 && !chips && multiple && item">
          {{ selectedRating.length }} selected
        </span>
      </template>
      <template v-slot:item="{ active, item, attrs, on }">
        <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
          <v-list-item-action v-if="multiple">
            <v-checkbox :ripple="false" :input-value="active"></v-checkbox>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              <v-row no-gutters align="center">
                <span :class="{ 'ml-5': item.parentId }">{{
                  item.display
                }}</span>
                <div v-if="showLevels" class="ml-5 risk-rating-level-chip">
                  <v-chip-group
                    v-model="item.selectedLevels"
                    mandatory
                    multiple
                    @click.native.stop.prevent="handleChangeRatingLevel"
                  >
                    <v-chip
                      v-for="(level, index) in riskLevels"
                      :key="`level-${index}`"
                      :value="level.value"
                      filter
                      filter-icon="fas fa-check-circle"
                      outlined
                      small
                      :color="level.bgColor"
                      ><span>{{ level.name }}</span></v-chip
                    >
                  </v-chip-group>
                </div>
              </v-row>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>

<script>
import lodashRemove from "lodash/remove";

import { getRiskRatingCategories } from "@/services/controlRisks/risks.service";
import {
  RISK_RATING_PRIMARIES,
  RISK_LEVELS,
} from "@/common/constants/controlRisks/risks";

const EMITTED_ACTIONS = {
  CHANGE: "change",
  DELETE: "delete",
  CLEAR: "clear",
};

export default {
  name: "RiskRatingPicker",
  components: {},
  props: {
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
    givenRating: {
      type: Object,
    },
    selectedRatings: {
      type: Array,
    },
    updatedRatings: {
      type: Array,
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
      default: "",
    },
    chips: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    showLevels: {
      type: Boolean,
      default: false,
    },
    hideCyberSubs: {
      type: Boolean,
      default: false,
    },
    hideMaritimeSubs: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const defaultLevelSelection = RISK_LEVELS.map((rating) => {
      return rating.value;
    });
    return {
      riskTypes: null,
      selectedRating: null,
      riskLevels: RISK_LEVELS,
      defaultLevelSelection: defaultLevelSelection,
    };
  },
  async mounted() {
    await this.getFilteredRiskTypes();
  },
  methods: {
    async getFilteredRiskTypes() {
      let riskRatingCategories = await getRiskRatingCategories();
      if (this.hideCyberSubs) {
        lodashRemove(riskRatingCategories, (r) => {
          return r.parentId === RISK_RATING_PRIMARIES.cyber;
        });
      }

      if (this.hideMaritimeSubs) {
        lodashRemove(riskRatingCategories, (r) => {
          return r.parentId === RISK_RATING_PRIMARIES.maritime;
        });
      }

      if (this.givenRating) {
        this.selectedRating = this.givenRating;
      }
      if (this.selectedRatings && this.multiple) {
        this.selectedRating = this.selectedRatings;
      }
      if (this.showLevels && this.multiple) {
        riskRatingCategories = riskRatingCategories.map((rating) => {
          rating.selectedLevels =
            this.selectedRatings?.find((r) => r.id === rating.id)
              ?.selectedLevels ?? this.defaultLevelSelection;
          return rating;
        });
      }

      this.riskTypes = riskRatingCategories;
    },
    handleChangeRatingLevel() {
      if (this.selectedRating) {
        this.handleChangeRating();
      }
    },
    handleChangeRating() {
      this.$emit(EMITTED_ACTIONS.CHANGE, this.selectedRating);
    },
    handleClickDeleteRating(index) {
      this.$emit(EMITTED_ACTIONS.DELETE, index);
    },
    handleClickClearRating() {
      this.$emit(EMITTED_ACTIONS.CLEAR);
    },
  },
  watch: {
    givenRating: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.selectedRating = newVal;
        }
      },
      deep: true,
    },
    updatedRatings: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.selectedRating = newVal;
        }
      },
      deep: true,
    },
  },
};
</script>

<style>
.rating-list .v-select__selection {
  font-size: 24px !important;
  line-height: 28px !important;
}
.rating-list .v-text-field__details {
  display: none !important;
}
</style>

<style scoped>
>>> .risk-rating-level-chip .v-icon {
  font-size: 14px;
}
>>> .risk-rating-level-chip .v-chip__content span {
  color: #000000;
}
>>> .risk-rating-level-chip .v-chip--active .v-chip__content span {
  font-weight: bold;
}
>>> .risk-rating-level-chip .v-chip--active {
  border-width: 1.5px;
}
</style>
